import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { MarketingShell } from '../components/marketing-shell.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useI18n } from '../lib/use-i18n.js';

/**
 * /legal/{tos,privacy,agent-agreement} — the platform's operative legal terms.
 *
 * Real, MOR-aware terms for the Kilat platform operated by Fractal Digital
 * Agency: operator Terms of Service (incl. merchant-of-record payment handling
 * + customer refund/chargeback allocation), a PDPA-2010 Privacy Policy, and the
 * independent-contractor Sales Agent Agreement. Entity name / registration
 * number / address flow from `platformBrands.legal` (fill the SSM + tax fields
 * in the seed before production — see config-seed/src/platform-brand.ts).
 *
 * Two clauses carry genuine regulatory weight and warrant a Malaysian lawyer's
 * sign-off before scale: (1) §4/§8 MOR payment collection + refunds (money-
 * transmission / e-money exposure), and (2) the agent independent-contractor
 * classification. The copy below is written to be operative, not placeholder.
 */

const LAST_UPDATED = '2026-07-05';

export function TosPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.legal.tos.metaTitle', { brand: brand.name }),
    description: t('marketing.legal.tos.metaDescription', { brand: brand.name }),
  });
  return (
    <MarketingShell>
      <LegalPage
        title={t('marketing.legal.tos.title')}
        subtitle={t('marketing.legal.tos.subtitle', { brand: brand.name })}
        sections={tosSections(
          brand.name,
          brand.legal.companyName ?? brand.name,
          brand.contact.email,
        )}
      />
    </MarketingShell>
  );
}

export function PrivacyPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.legal.privacy.metaTitle', { brand: brand.name }),
    description: t('marketing.legal.privacy.metaDescription', { brand: brand.name }),
  });
  return (
    <MarketingShell>
      <LegalPage
        title={t('marketing.legal.privacy.title')}
        subtitle={t('marketing.legal.privacy.subtitle', { brand: brand.name })}
        sections={privacySections(
          brand.name,
          brand.legal.companyName ?? brand.name,
          brand.contact.email,
        )}
      />
    </MarketingShell>
  );
}

export function AgentAgreementPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.legal.agentAgreement.metaTitle', { brand: brand.name }),
    description: t('marketing.legal.agentAgreement.metaDescription', { brand: brand.name }),
  });
  return (
    <MarketingShell>
      <LegalPage
        title={t('marketing.legal.agentAgreement.title')}
        subtitle={t('marketing.legal.agentAgreement.subtitle', { brand: brand.name })}
        sections={agentAgreementSections(
          brand.name,
          brand.legal.companyName ?? brand.name,
          brand.contact.email,
        )}
      />
    </MarketingShell>
  );
}

// ── presentation ────────────────────────────────────────────────────────

interface Section {
  readonly heading: string;
  readonly body: readonly string[];
}

function LegalPage({
  title,
  subtitle,
  sections,
}: {
  readonly title: string;
  readonly subtitle: string;
  readonly sections: readonly Section[];
}) {
  const { t } = useI18n();
  return (
    <article
      style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: 'var(--space-12) var(--space-6)',
      }}
    >
      <header>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-primary)',
            letterSpacing: 3,
            textTransform: 'uppercase',
            fontWeight: 600,
            margin: 0,
          }}
        >
          {t('marketing.legal.eyebrow')}
        </p>
        <h1
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-h1)',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h1>
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </p>
        <p
          style={{
            margin: 'var(--space-4) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('marketing.legal.lastUpdated')} {LAST_UPDATED}
        </p>
      </header>
      <div style={{ marginTop: 'var(--space-8)', display: 'grid', gap: 'var(--space-6)' }}>
        {sections.map((s) => (
          <section key={s.heading}>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--text-h2)',
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              {s.heading}
            </h2>
            {s.body.map((p, i) => (
              <p
                key={i}
                style={{
                  margin: 'var(--space-3) 0 0 0',
                  fontSize: 'var(--text-body)',
                  color: 'var(--color-on-surface)',
                  lineHeight: 1.7,
                }}
              >
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>
    </article>
  );
}

// ── content ─────────────────────────────────────────────────────────────

function tosSections(brand: string, legal: string, email: string): readonly Section[] {
  return [
    {
      heading: '1. Who these terms are between',
      body: [
        `The ${brand} platform ("${brand}", "the Service") is owned and operated by ${legal} ("we", "us", "our"), a company registered in Malaysia. These Terms of Service form a binding contract between us and the carwash business that registers for the Service ("you", "the Operator").`,
        `By creating an account, or by using the Service after these terms take effect, you confirm you are authorised to bind the Operator and you accept these terms. If you do not accept them, do not use the Service.`,
      ],
    },
    {
      heading: '2. The Service',
      body: [
        `${brand} is a software platform for carwash operators providing: digital queue and job management, a point-of-sale, a loyalty and voucher engine, marketing and notification tools, multi-outlet subscription billing, and a white-label customer web app (PWA) that carries your brand.`,
        `We provide the Service on a commercially reasonable, best-effort basis and do not guarantee a specific uptime unless a separate written service-level agreement is in place. We may add, change, or remove features, and will give reasonable notice of material changes or planned maintenance via in-app notice and/or email.`,
      ],
    },
    {
      heading: '3. Free trial, subscription and fees',
      body: [
        `New Operators receive a 14-day free trial. After the trial the Service is charged as a monthly subscription at the plan rate published at /pricing (or as otherwise agreed in writing). Additional outlets added mid-cycle are prorated to the day.`,
        `Invoices are issued each billing cycle and are payable by the due date shown on the invoice, using the payment method we make available in the operator dashboard (which may include an online gateway or a manual bank transfer confirmed by us). All fees are exclusive of any applicable Malaysian taxes (e.g. SST), which you are responsible for where chargeable.`,
        `If an invoice is unpaid by its due date the account becomes past-due; after 30 days past-due the account may be suspended; after 90 days suspended the account may be archived. Suspension limits new bookings and dashboard functions but preserves your data during the retention window.`,
      ],
    },
    {
      heading: '4. Customer payments — we act as your Merchant of Record',
      body: [
        `Where you enable online customer payments, ${legal} acts as your Merchant of Record ("MOR") and payment collection agent. This means the end customer pays ${legal} (through our licensed payment processor, currently toyyibPay) for the wash service, and ${legal} then remits the corresponding amount to you. You will never need to sign up for or operate your own payment gateway.`,
        `${legal} does not take a commission on the wash price — you receive the full price you set. The customer separately pays a visible payment-processing fee to cover the gateway cost; that fee is disclosed to the customer at checkout and is not part of your revenue. Our only charge to you is the SaaS subscription in §3.`,
        `You authorise ${legal} to collect payments from your customers on your behalf, to issue receipts, and to deduct refunds, reversals, and chargebacks attributable to your services from amounts otherwise payable to you. Collected funds are remitted to your nominated bank account on the settlement schedule shown in your dashboard, net of any such deductions and of amounts placed on hold to cover disputes.`,
        `You remain solely responsible for actually performing the wash services your customers pay for, for the accuracy of your prices, and for any consumer-law obligations owed to your customers in respect of those services.`,
      ],
    },
    {
      heading: '5. Refunds, disputes and chargebacks',
      body: [
        `Because the customer contracts with you for the wash service, decisions about refunds for service quality, no-shows, or cancellations are yours, made in line with your posted refund policy and applicable consumer law. ${legal} provides the mechanism to process approved refunds through the original payment method.`,
        `If a customer initiates a chargeback or payment dispute relating to your service, you are responsible for the disputed amount and any associated processor fee; ${legal} may recover these from amounts payable to you or invoice you for them. ${legal} is responsible only for correctly processing and remitting payments, not for the underlying service dispute.`,
      ],
    },
    {
      heading: '6. Data ownership and processing',
      body: [
        `You own your Operator data (business details, outlets, catalogue, prices) and the customer data collected through the Service on your behalf (vehicle plates, ticket history, loyalty balances, contact details). ${legal} processes this data to provide the Service and as described in our Privacy Policy, and does not use it for unrelated purposes or sell it.`,
        `You are the data controller/user for your customers' personal data under the PDPA; ${legal} acts as your data processor for that data, save where ${legal} determines the purposes and means of processing (for example, platform security, fraud prevention, and payment settlement), in which case ${legal} is the data user. You can export your data during the subscription; after cancellation it is retained for 90 days and then deleted, except records we must keep for legal, tax, or anti-fraud reasons.`,
      ],
    },
    {
      heading: '7. Acceptable use',
      body: [
        `You will not use the Service to break the law, to send customers marketing beyond the platform's frequency limits or without a lawful basis, to reverse-engineer or resell the platform, to circumvent fees, or to upload unlawful, infringing, or harmful content.`,
        `We may suspend or terminate an account that violates these terms, that presents a security or fraud risk, or that we are required to act on by law, with notice where practicable.`,
      ],
    },
    {
      heading: '8. Warranties and limitation of liability',
      body: [
        `The Service is provided "as is" and "as available". To the maximum extent permitted by Malaysian law, we exclude all implied warranties. Nothing in these terms limits liability that cannot be limited by law.`,
        `Subject to the above, our total aggregate liability to you for all claims arising out of or relating to the Service in any 12-month period is limited to the subscription fees you paid to us in that period. We are not liable for loss of profits, loss of revenue, loss of goodwill, or indirect or consequential loss. For clarity, amounts we collect and hold as your Merchant of Record remain due to you and are not capped by this clause — that is your money, held on your behalf, not our liability.`,
      ],
    },
    {
      heading: '9. Indemnity',
      body: [
        `You will indemnify ${legal} against third-party claims arising from your wash services, your prices or promotions, your content, your breach of these terms, or your breach of applicable law (including consumer-protection and data-protection law) in respect of your customers.`,
      ],
    },
    {
      heading: '10. Term and termination',
      body: [
        `Either party may terminate the subscription at any time; termination takes effect at the end of the current billing cycle and does not refund fees already due. On termination we stop the Service, settle any customer funds properly due to you (less deductions under §4–§5), and handle your data per §6.`,
      ],
    },
    {
      heading: '11. Changes to these terms',
      body: [
        `We may update these terms and will give at least 30 days notice of material changes via in-app banner and/or email. Continued use of the Service after the stated effective date constitutes acceptance of the updated terms.`,
      ],
    },
    {
      heading: '12. Governing law',
      body: [
        `These terms are governed by the laws of Malaysia, and the courts of Kuala Lumpur have exclusive jurisdiction over any dispute, without prejudice to any mandatory consumer rights.`,
      ],
    },
    {
      heading: '13. Contact',
      body: [`Questions about these terms: ${email} (${legal}).`],
    },
  ];
}

function privacySections(brand: string, legal: string, email: string): readonly Section[] {
  return [
    {
      heading: '1. Who we are',
      body: [
        `This policy explains how ${legal}, which operates the ${brand} platform, handles personal data, in line with Malaysia's Personal Data Protection Act 2010 ("PDPA"). It covers operators, their customers (data handled on the operator's behalf), and sales agents. For personal data of an operator's customers, the operator is the data user and ${legal} acts as data processor, except where ${legal} determines the purpose (security, fraud prevention, payment settlement).`,
      ],
    },
    {
      heading: '2. What we collect',
      body: [
        `From operators: business name, owner/contact name, email, phone, business/billing address, bank account details for payment settlement, and account activity.`,
        `From customers (on the operator's behalf): vehicle plate and type, phone number (when phone-verified), ticket and wash history, loyalty balances, notification preferences, and — for online payments — the transaction record (amount, time, reference). Card and bank credentials are entered directly with our payment processor and are not seen or stored by us.`,
        `From sales agents: name, phone, email, national identity information (NRIC) and bank details required for KYC and commission payouts.`,
        `Automatically: IP address, device/browser type, and operational logs used for security, debugging, and abuse prevention.`,
      ],
    },
    {
      heading: '3. How and why we use it',
      body: [
        `To provide the Service: queue and job management, billing, loyalty, marketing you initiate, commission tracking, and — for wash payments where we act as Merchant of Record — collecting payment from customers and remitting it to the operator.`,
        `To keep the platform secure and prevent fraud, to meet legal, tax and accounting obligations, and to improve the product using aggregated, de-identified data. We do not sell personal data.`,
      ],
    },
    {
      heading: '4. Payment data',
      body: [
        `Online wash payments are processed by our licensed payment processor (currently toyyibPay). ${legal}, as Merchant of Record, receives and stores transaction records (amounts, references, status) needed to settle funds to operators and to handle refunds and disputes. Sensitive payment credentials (card numbers, banking logins) are handled by the processor under its own security standards and are not stored by ${legal}.`,
      ],
    },
    {
      heading: '5. Where data is stored and how it is protected',
      body: [
        `Data is stored on Google Cloud infrastructure in the asia-southeast1 (Singapore) region. Access is enforced by per-tenant security rules — each operator can access only their own tenant's data — and platform administrators access data only as needed to provide support, ensure security, or meet legal obligations.`,
      ],
    },
    {
      heading: '6. Who we share it with',
      body: [
        `Service providers who help us run the platform, under confidentiality obligations: Google Cloud (hosting), our payment processor (toyyibPay) and banks/DuitNow for settlement and payouts, and notification providers (Firebase Cloud Messaging, and, if enabled, WhatsApp/SMS providers). We also disclose data where required by law or to protect our or others' rights. We do not otherwise share personal data.`,
      ],
    },
    {
      heading: '7. Cross-border transfer',
      body: [
        `Because our infrastructure and some processors are located outside Malaysia (including Singapore), your personal data may be transferred and processed outside Malaysia. Where we do so we take steps consistent with the PDPA to ensure a comparable level of protection.`,
      ],
    },
    {
      heading: '8. Retention',
      body: [
        `We keep personal data for as long as needed to provide the Service and to meet legal, tax, and anti-fraud obligations. After an account is cancelled, associated data is retained for 90 days and then deleted, except records we are required or entitled to keep for longer.`,
      ],
    },
    {
      heading: '9. Your PDPA rights',
      body: [
        `Subject to the PDPA, you may request access to and correction of your personal data, withdraw consent or limit processing, and, where applicable, request deletion. Customers of an operator should first contact that operator (the data user); we will assist as processor. To exercise a right or raise a concern, email ${email}.`,
        `You may also lodge a complaint with the Personal Data Protection Commissioner of Malaysia.`,
      ],
    },
    {
      heading: '10. Cookies and local storage',
      body: [
        `The apps use local storage and IndexedDB for sign-in sessions and offline caching (e.g. loyalty data). We do not set third-party advertising cookies.`,
      ],
    },
    {
      heading: '11. Changes and contact',
      body: [
        `We will notify material changes to this policy in-app and/or by email at least 30 days before they take effect. Privacy questions or requests: ${email} (${legal}).`,
      ],
    },
  ];
}

function agentAgreementSections(brand: string, legal: string, email: string): readonly Section[] {
  return [
    {
      heading: '1. Independent contractor',
      body: [
        `You (the sales agent) are engaged by ${legal} as an independent contractor, not an employee. Nothing in this agreement creates an employment, partnership, or agency relationship beyond the limited authority to introduce ${brand} as described below. You are solely responsible for your own income tax, EPF, SOCSO, and any other statutory contributions.`,
      ],
    },
    {
      heading: '2. What you do',
      body: [
        `You introduce the ${brand} platform to carwash operators using your unique agent code or QR link. When an operator signs up using your code, they are attributed to you on their tenant record. Attribution is recorded at signup and is not transferable.`,
        `You must describe ${brand} accurately and only as authorised, must not make promises on our behalf that are not in our published materials, and must comply with all applicable laws when promoting the Service.`,
      ],
    },
    {
      heading: '3. Commission',
      body: [
        `For each attributed operator you earn a one-time commission equal to one full month of that operator's subscription, credited when that operator pays their first invoice. There is no recurring or lifetime commission — the commission is earned once, per operator.`,
        `Tier multipliers set out in ${brand}'s published commission rules (e.g. Bronze 1.0× / Silver 1.1× / Gold 1.25×) may apply to the base commission. We may change the commission model or multipliers on 30 days notice via the Agent app; changes apply only to signups occurring after the effective date — commissions already earned are not altered.`,
      ],
    },
    {
      heading: '4. Payouts',
      body: [
        `Earned commission is paid monthly, after the relevant operator invoice has settled, to the bank details you provide in the Agent app, by DuitNow or bank transfer. A minimum payout threshold applies (RM 100); balances below the threshold roll over to the next cycle. You must complete identity verification (KYC) before your first payout.`,
      ],
    },
    {
      heading: '5. Clawback',
      body: [
        `If an operator's qualifying invoice is refunded or reversed within 30 days of payment, the related commission is reversed in full or in proportion to the refund, and may be recovered from future payouts.`,
      ],
    },
    {
      heading: '6. Anti-fraud',
      body: [
        `You must not: sign up operators you control using your own code to generate commission, submit fake or non-genuine signups, misrepresent the Service, impersonate ${legal} or its staff, or operate multiple agent accounts to game attribution.`,
        `Breach may result in immediate suspension, forfeiture of unpaid commission, clawback of amounts paid on fraudulent signups, and termination. We may withhold payouts pending investigation of suspected abuse.`,
      ],
    },
    {
      heading: '7. Confidentiality and data',
      body: [
        `You will keep non-public information about ${brand}, operators, and customers confidential, use it only to perform under this agreement, and handle any personal data you receive in line with the PDPA and our Privacy Policy.`,
      ],
    },
    {
      heading: '8. Term and termination',
      body: [
        `Either party may terminate this agreement at any time on written notice. On termination, commission accrual stops immediately; commission already earned and above the payout threshold, and not subject to clawback, is paid in the next scheduled cycle.`,
      ],
    },
    {
      heading: '9. Governing law and contact',
      body: [
        `This agreement is governed by the laws of Malaysia and the courts of Kuala Lumpur have jurisdiction. Questions: ${email} (${legal}).`,
      ],
    },
  ];
}
